import { useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { sourcesApi } from '../api/sources'
import { useInfiniteQuotes } from '../hooks/useQuotes'
import { QuoteList } from '../components/QuoteList'

export const SourceDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const sourceId = Number(id)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { data: source, isLoading: isLoadingSource } = useQuery({
    queryKey: ['sources', sourceId],
    queryFn: () => sourcesApi.getSource(sourceId),
    enabled: !!sourceId,
  })

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuotes(20, undefined, sourceId)
  const quotes = data?.pages.flatMap((page) => page.items) ?? []

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoadingSource) return <p>Laden...</p>
  if (!source) return <p>Quelle nicht gefunden.</p>

  return (
    <div>
      <div style={{ marginBottom: '0.5rem' }}>
        <Link to="/sources" style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textDecoration: 'none' }}>
          ← Alle Quellen
        </Link>
      </div>

      <div className="card" style={{ marginBottom: '2rem', padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 'var(--radius-sm)',
            background: source.type === 'book' ? 'var(--primary)' : '#6b7280',
            color: 'white', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0,
          }}>
            {source.type === 'book' ? '📖' : '📄'}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, marginBottom: '0.25rem' }}>{source.title}</h2>
            {source.authors?.length > 0 && (
              <div style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                {source.authors.map((a) => (
                  <Link
                    key={a.id}
                    to={`/authors/${a.id}`}
                    style={{ color: 'var(--primary)', textDecoration: 'none', marginRight: '0.5rem' }}
                  >
                    {a.name}
                  </Link>
                ))}
              </div>
            )}
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {source.year && <span>{source.year}</span>}
              {source.publisher && <span>{source.publisher}</span>}
              {source.isbn && <span>ISBN: {source.isbn}</span>}
              {source.doi && <span>DOI: {source.doi}</span>}
              {source.issn && <span>ISSN: {source.issn}</span>}
            </div>
          </div>
        </div>
      </div>

      <h3 style={{ marginBottom: '1rem' }}>
        Zitate {!isLoading && data && `(${data.pages[0]?.total ?? 0})`}
      </h3>
      <QuoteList
        quotes={quotes}
        isLoading={isLoading}
        error={error}
        isFetchingNextPage={isFetchingNextPage}
      />
      <div ref={sentinelRef} style={{ height: '1px' }} />
    </div>
  )
}
